import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import type {
    SketchCircularPatternConstraintInstanceParameters,
    SketchCircularPatternFormulaPayload,
} from '../../../../types/sketch/circular-pattern/SketchCircularPatternConstraintTypes';
import type {SketchParametricPointHandle} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchRelationKind} from '../constraint/relation-compatibility/SketchRelationKinds';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/**
 * 草图阵列结构关系公式编译器。
 *
 * 关系本身不区分点、直线、圆、圆弧、椭圆、抛物线或样条。系统构建器把每个
 * 实体适配为有序定义点和可选半径参数，本编译器只按“种子目标组 ↔ 实例目标组”
 * 生成统一刚体变换方程。复合实体在关系创建边界展开为基础成员，因此仍共享
 * 同一个阵列定义和同一条 Patterned 聚合关系。
 */
export class SketchPatternConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 本编译器处理的关系类型。 */
    public readonly relationKinds = [SketchRelationKind.Patterned];

    /**
     * 编译圆周阵列聚合关系。
     *
     * @param context 关系编译上下文。
     * @returns 关系已编译、已拒绝或不适用。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        if (context.constraintState.relationKind !== SketchRelationKind.Patterned) {
            return 'not-applicable';
        }

        const payload = this.resolvePayload(context);
        if (!payload) {
            return 'rejected';
        }
        const directTargetRefs = [...context.resolveDirectTargetRefs()];
        let centerHandle: SketchParametricPointHandle | undefined;
        if (payload.hasCenterPointReference !== false) {
            const centerRef = directTargetRefs.shift();
            centerHandle = centerRef?.targetKind === 'point'
                ? context.pointHandles.get(centerRef.targetId)
                : undefined;
            if (!centerHandle) {
                context.reportUnsupported('圆周阵列聚合关系缺少有效的中心点引用');
                return 'rejected';
            }
        } else {
            const center2d = payload.center2d ?? [0, 0];
            centerHandle = context.system.addPoint(
                `constraint:${context.constraintState.constraintId}:fixed-center`,
                center2d[0],
                center2d[1],
                true,
            );
        }
        const seedTargetKinds = payload.seedTargetKinds ?? ['entity'];
        const seedTargetCount = seedTargetKinds.length;
        if (seedTargetCount === 0
            || directTargetRefs.length !== seedTargetCount * (payload.instances.length + 1)) {
            context.reportUnsupported('圆周阵列聚合关系的种子组与实例组目标数量不一致');
            return 'rejected';
        }
        const resolveTargetPointHandles = (
            targetRef: (typeof directTargetRefs)[number],
        ): readonly SketchParametricPointHandle[] => {
            if (targetRef.targetKind === 'point') {
                const pointHandle = context.pointHandles.get(targetRef.targetId);
                return pointHandle ? [pointHandle] : [];
            }
            const lineHandles = context.linePointHandles.get(targetRef.targetId);
            if (lineHandles) {
                // 直线使用专用有序端点表维护起终点语义，不会重复登记到通用实体点表。
                return [...lineHandles];
            }
            return [...(context.entityPointHandles.get(targetRef.targetId) ?? [])];
        };
        const seedRefs = directTargetRefs.slice(0, seedTargetCount);
        const seedHandlesByTarget = seedRefs.map((targetRef, targetIndex) => {
            if (targetRef.targetKind !== seedTargetKinds[targetIndex]) {
                return [];
            }
            return resolveTargetPointHandles(targetRef);
        });
        if (seedHandlesByTarget.some((handles) => handles.length === 0)) {
            context.reportUnsupported('圆周阵列种子组包含无法参数化的草图实体');
            return 'rejected';
        }
        const uniqueSeedPointMap = new Map<string, SketchParametricPointHandle>();
        seedHandlesByTarget.flat().forEach((handle) => {
            uniqueSeedPointMap.set(`${handle.xId}/${handle.yId}`, handle);
        });
        const uniqueSeedPoints = [...uniqueSeedPointMap.values()];
        const equationPrefix = `constraint:${context.constraintState.constraintId}:circular-pattern`;
        for (let instanceOffset = 0; instanceOffset < payload.instances.length; instanceOffset += 1) {
            const instanceParameters = payload.instances[instanceOffset]!;
            const instanceRefs = directTargetRefs.slice(
                seedTargetCount * (instanceOffset + 1),
                seedTargetCount * (instanceOffset + 2),
            );
            for (let targetIndex = 0; targetIndex < seedTargetCount; targetIndex += 1) {
                const instanceRef = instanceRefs[targetIndex]!;
                if (instanceRef.targetKind !== seedTargetKinds[targetIndex]) {
                    context.reportUnsupported(
                        `圆周阵列实例 ${instanceParameters.instanceIndex} 的目标类型与种子组不一致`,
                    );
                    return 'rejected';
                }
                const seedHandles = seedHandlesByTarget[targetIndex]!;
                const instanceHandles = resolveTargetPointHandles(instanceRef);
                if (instanceHandles.length !== seedHandles.length || instanceHandles.length === 0) {
                    context.reportUnsupported(
                        `圆周阵列实例 ${instanceParameters.instanceIndex} 的参数点数量与种子组不一致`,
                    );
                    return 'rejected';
                }
                seedHandles.forEach((seedHandle, pointIndex) => {
                    const instanceHandle = instanceHandles[pointIndex]!;
                    const equationId = `${equationPrefix}:instance-${instanceParameters.instanceIndex}`
                        + `:target-${targetIndex}:point-${pointIndex}`;
                    context.system.addEquation(payload.rotateInstances === false
                        ? SketchParametricEquationFactory.translatedPointByRotatedGroupAnchor(
                            equationId,
                            seedHandle,
                            instanceHandle,
                            centerHandle!,
                            uniqueSeedPoints,
                            instanceParameters.rotationRad,
                        )
                        : SketchParametricEquationFactory.rotatedPointAroundCenter(
                            equationId,
                            seedHandle,
                            instanceHandle,
                            centerHandle!,
                            instanceParameters.rotationRad,
                        ));
                });
                if (seedRefs[targetIndex]!.targetKind === 'entity') {
                    const seedRadiusId = context.radiusParameterIds.get(seedRefs[targetIndex]!.targetId);
                    const instanceRadiusId = context.radiusParameterIds.get(instanceRef.targetId);
                    if (seedRadiusId && instanceRadiusId) {
                        context.system.addEquation(SketchParametricEquationFactory.scalarEqual(
                            `${equationPrefix}:instance-${instanceParameters.instanceIndex}`
                            + `:target-${targetIndex}:radius`,
                            instanceRadiusId,
                            seedRadiusId,
                        ));
                    }
                }
            }
        }
        return 'compiled';
    }

    /**
     * 读取并校验圆周阵列公式载荷。
     *
     * @param context 关系编译上下文。
     * @returns 有效载荷；无效时返回 null 并写入诊断。
     */
    private resolvePayload(
        context: SketchConstraintFormulaCompileContext,
    ): SketchCircularPatternFormulaPayload | null {
        const payload = context.constraintState.formulaPayload;
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
            context.reportUnsupported('圆周阵列聚合关系缺少参数公式载荷');
            return null;
        }
        const kind = Reflect.get(payload, 'kind');
        const patternPersistentId = Reflect.get(payload, 'patternPersistentId');
        const rawInstances = Reflect.get(payload, 'instances');
        if (kind !== 'circular-pattern'
            || typeof patternPersistentId !== 'string'
            || patternPersistentId.length === 0
            || !Array.isArray(rawInstances)
            || rawInstances.length === 0) {
            context.reportUnsupported('圆周阵列聚合关系的参数公式载荷非法');
            return null;
        }
        const instanceIndexSet = new Set<number>();
        const instances: SketchCircularPatternConstraintInstanceParameters[] = [];
        for (const rawInstance of rawInstances) {
            if (!rawInstance || typeof rawInstance !== 'object' || Array.isArray(rawInstance)) {
                context.reportUnsupported('圆周阵列聚合关系包含非法实例参数');
                return null;
            }
            const instanceIndex = Reflect.get(rawInstance, 'instanceIndex');
            const rotationRad = Reflect.get(rawInstance, 'rotationRad');
            if (typeof instanceIndex !== 'number'
                || !Number.isInteger(instanceIndex)
                || instanceIndex <= 0
                || instanceIndexSet.has(instanceIndex)
                || typeof rotationRad !== 'number'
                || !Number.isFinite(rotationRad)) {
                context.reportUnsupported('圆周阵列聚合关系包含非法或重复的实例参数');
                return null;
            }
            instanceIndexSet.add(instanceIndex);
            instances.push({instanceIndex, rotationRad});
        }
        const rawCenter2d = Reflect.get(payload, 'center2d');
        const rawHasCenterPointReference = Reflect.get(payload, 'hasCenterPointReference');
        const rawSeedTargetKinds = Reflect.get(payload, 'seedTargetKinds');
        const rawRotateInstances = Reflect.get(payload, 'rotateInstances');
        const center2d = Array.isArray(rawCenter2d)
            && rawCenter2d.length === 2
            && rawCenter2d.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate))
            ? [rawCenter2d[0], rawCenter2d[1]] as const
            : undefined;
        const seedTargetKinds = Array.isArray(rawSeedTargetKinds)
            && rawSeedTargetKinds.length > 0
            && rawSeedTargetKinds.every((targetKind) => targetKind === 'entity' || targetKind === 'point')
            ? rawSeedTargetKinds as ('entity' | 'point')[]
            : undefined;
        if (rawHasCenterPointReference === false && !center2d) {
            context.reportUnsupported('圆周阵列聚合关系固定中心坐标非法');
            return null;
        }
        return {
            kind: 'circular-pattern',
            patternPersistentId,
            ...(center2d ? {center2d} : {}),
            ...(typeof rawHasCenterPointReference === 'boolean'
                ? {hasCenterPointReference: rawHasCenterPointReference}
                : {}),
            ...(seedTargetKinds ? {seedTargetKinds} : {}),
            ...(typeof rawRotateInstances === 'boolean' ? {rotateInstances: rawRotateInstances} : {}),
            instances,
        };
    }
}
